import { loadFeaturesByLayerOfflineAware, type OfflineFeature } from '@/lib/offline/offlineFeatures';
import { useQueries } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

// While a project's vector layers stream in (one query per layer), merge them
// into the returned list at most this often. Every merge hands the viewers a
// new array, which rebuilds the 2D map source / 3D scene from scratch, so
// merging per layer is quadratic in the layer count (254 layers on some CAD
// projects).
export const FEATURE_MERGE_THROTTLE_MS = 500;

/**
 * Features of every given vector layer, one `['features', layerId]` query per
 * layer, flattened into one array whose identity only changes when the data
 * does. Settled changes (visibility toggles, edits) land at once; a burst of
 * layers arriving lands every FEATURE_MERGE_THROTTLE_MS.
 */
export function useLayerFeatures(layerIds: string[], projectId: string): OfflineFeature[] {
  const featureQueries = useQueries({
    queries: layerIds.map((layerId) => ({
      queryKey: ['features', layerId],
      queryFn: () => loadFeaturesByLayerOfflineAware([layerId], projectId),
    })),
  });

  // Stable key: only recompute when query success states or data references actually change
  const featureDataKey = featureQueries.map((q) => `${q.isSuccess}:${q.dataUpdatedAt}`).join('|');
  const featuresSettled = featureQueries.every((q) => !q.isPending);
  const [mergedKey, setMergedKey] = useState(featureDataKey);
  const lastMergeRef = useRef(0);
  useEffect(() => {
    if (featureDataKey === mergedKey) return;
    const wait = featuresSettled ? 0 : Math.max(0, lastMergeRef.current + FEATURE_MERGE_THROTTLE_MS - Date.now());
    const timer = setTimeout(() => {
      lastMergeRef.current = Date.now();
      setMergedKey(featureDataKey);
    }, wait);
    return () => clearTimeout(timer);
  }, [featureDataKey, mergedKey, featuresSettled]);

  return useMemo(
    () => featureQueries.flatMap((q) => q.data ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mergedKey],
  );
}
